import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import React, { useState } from 'react';

interface FaqItem {
  question: string;
  answer: string | React.ReactNode;
}

interface FaqCategory {
  /** Stable id — `title` is translated, so it can't be matched against. */
  key: string;
  title: string;
  items: FaqItem[];
}

// Categories whose answers link to Stripe, /pricing and /settings/billing.
// Fine on the web; inside the iOS app they are exactly the "call to action
// directing customers to a purchasing mechanism other than IAP" that App Store
// guideline 3.1.3(b) forbids, so the app opens /support?from=app to drop them.
const PURCHASE_CATEGORY_KEYS = ['account-billing', 'subscription-credits'];

const InternalFaq = ({ hidePurchaseTopics = false }: { hidePurchaseTopics?: boolean }) => {
  const { t } = useLocale('support');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(
    new Set(),
  );

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const allCategories: FaqCategory[] = [
    {
      key: 'account-billing',
      title: t('Account & Billing'),
      items: [
        {
          question: t('How do I cancel my subscription?'),
          answer: t('cancel-subscription-answer'),
        },
        {
          question: t('How can I update my payment details?'),
          answer: t('update-payment-details-answer'),
        },
        {
          question: t('What payment methods are accepted?'),
          answer: t(
            'We support all major credit cards and debit cards. Your payment method is secured by Stripe for your privacy and security. For PayPal, Alipay, WeChat, GrabPay, YooMoney payments, please contact us.',
          ),
        },

        {
          question: t('Are there any hidden fees?'),
          answer: t(
            'No, there are no hidden fees. You just pay the monthly subscription fee.',
          ),
        },
        {
          question: t('Where can I find my Favorites?'),
          answer: t('find-favorites-answer'),
        },
        {
          question: t('How do I reset my password?'),
          answer: t(
            'You can reset your password by clicking the "Forgot Password?" link on the login page. Enter your email address and follow the instructions sent to your inbox.',
          ),
        },
        {
          question: t('Can I change my email address?'),
          answer: t(
            'Yes, you can update your email address in your account settings. Navigate to Settings > Profile to make changes to your account information.',
          ),
        },
      ],
    },
    {
      key: 'subscription-credits',
      title: t('Subscription & Credits'),
      items: [
        {
          question: t('Can I change my plan at any time?'),
          answer: t('Yes, you can upgrade or downgrade your plan at any time.'),
        },
        {
          question: t('What pricing plans are available?'),
          answer: t('pricing-plans-answer'),
        },
        {
          question: t(
            'If all of the credits are not used during the month, do they roll over to the next month?',
          ),
          answer: t(
            'No, unused credits do not roll over to the next month. Use them up.',
          ),
        },
        {
          question: t(
            'When upgrading plan will I pay full amount or just the difference?',
          ),
          answer: t(
            'When you upgrade your plan, you will only pay the difference between the two plans.',
          ),
        },
        {
          question: t(
            'Are the image credits, words, and text-to-speech characters allocated yearly or monthly?',
          ),
          answer: t(
            'The image credits, words, and text-to-speech characters are allocated monthly and reset on the same day each month when the subscription started.',
          ),
        },
        {
          question: t(
            'I used all my words/image credits/characters for text-to-speech, can I buy more?',
          ),
          answer: t(
            'To get more credits, you can upgrade your plan on the Subscription tab in your account settings.',
          ),
        },
        {
          question: t('Is there a free trial available?'),
          answer: t(
            'Yes, we offer a free plan with limited features so you can try our services before subscribing to a paid plan.',
          ),
        },
        {
          question: t('Can I get a refund?'),
          answer: t(
            'We offer refunds within 30 days of purchase if you have not used more than 10% of your monthly credits. Please contact support for assistance.',
          ),
        },
        {
          question: t(
            "What happens if I don't like a generated image or video?",
          ),
          answer: t(
            "If you're not happy with a generated image or video, simply give it a thumbs-down and we'll automatically credit back what you were charged for it. The confirmation message shows how many credits were returned. To keep things fair, downvote refunds are capped at 10% of your monthly credits each billing period; for a larger refund, just contact support.",
          ),
        },
      ],
    },
    {
      key: 'ai-image-generator',
      title: t('AI Image Generator'),
      items: [
        {
          question: t(
            'Can I use the AI-generated images for commercial purposes?',
          ),
          answer: t(
            'Yes, you can use AI-generated content for commercial purposes if you are subscribed to any of our paid plans. You own full rights to any images generated through our service if you are subscribed to any of our paid plans.',
          ),
        },
        {
          question: t('Can I upload my own image as a reference?'),
          answer: t(
            'Yes, you can upload your own image as a reference by clicking the "Upload image" button. And then in the prompt you can specify how you want the image to be generated. You can also select the desired action from the dropdown menu, for example, "Edit image", "Character Reference", "Replace Background", "Caricature" etc.',
          ),
        },
        {
          question: t('Can I edit generated images?'),
          answer: t(
            'Yes, you can edit generated images by clicking the edit icon on the image. Or by clicking on the image and then clicking on the "Edit image" button.',
          ),
        },
        {
          question: t('Can I animate generated images?'),
          answer: t(
            'Yes, you can animate generated images by clicking the animate icon on the image. Or by clicking on the image and then clicking on the "Generate Video" button.',
          ),
        },
        {
          question: t('How to download generated images?'),
          answer: t(
            'You can download generated images by clicking the download icon on the image. Or by clicking on the image and then clicking on the "Download image" button.',
          ),
        },
        {
          question: t('How to delete generated images?'),
          answer: t(
            'You can delete a generated image by clicking on the image and then clicking on the "Delete" button.',
          ),
        },
      ],
    },
    // {
    //   title: t('Text-to-Speech'),
    //   items: [
    //     {
    //       question: t('What languages are supported for text-to-speech?'),
    //       answer: t(
    //         'We support over 30 languages including English, Spanish, French, German, Italian, Portuguese, Russian, Chinese, Japanese, and Korean.',
    //       ),
    //     },
    //     {
    //       question: t('Can I adjust the voice speed and pitch?'),
    //       answer: t(
    //         'Yes, you can customize voice speed, pitch, and tone to match your preferences. These options are available in the text-to-speech settings.',
    //       ),
    //     },
    //     {
    //       question: t('What audio formats can I export?'),
    //       answer: t(
    //         'You can export your text-to-speech audio in MP3, WAV, and OGG formats.',
    //       ),
    //     },
    //     {
    //       question: t('Is there a character limit per conversion?'),
    //       answer: t(
    //         'The character limit per conversion depends on your plan. Free plans have a 500 character limit, while paid plans can process up to 5000 characters at once.',
    //       ),
    //     },
    //     {
    //       question: t('Can I use multiple voices in one project?'),
    //       answer: t(
    //         'Yes, premium plans allow you to use multiple voices within a single project for creating dialogues or varied narration.',
    //       ),
    //     },
    //   ],
    // },
    {
      key: 'ai-chat-marky',
      title: t('AI Chat with Marky'),
      items: [
        {
          question: t('What AI models are available for chat?'),
          answer: t(
            'We offer access to the best AI models from OpenAI, Anthropic, Google Gemini, xAI, LLaMA, DeepSeek, Kimi, Qwen and more. You can choose the model you want to use in your chat by clicking the model selector in the top left corner of the chat window.',
          ),
        },

        {
          question: t('Is there a word limit for AI writing?'),
          answer: t(
            'Word limits vary by plan. Free plans have a 4,000 word monthly limit, while premium plans offer up to unlimited words for some models.',
          ),
        },
        {
          question: t('Can the AI remember previous conversations?'),
          answer: t(
            'Yes, within the same chat session, the AI maintains context and can reference previous messages in the conversation.',
          ),
        },
        {
          question: t('Can I use custom prompts and templates?'),
          answer: t(
            'Yes, you can create and save custom prompts and templates for repeated use across your projects.',
          ),
        },
      ],
    },
    {
      key: 'resources',
      title: t('Resources'),
      items: [
        {
          question: t('Are there tutorials available?'),
          answer: t('tutorials-available-answer'),
        },
        {
          question: t('Is there an API available?'),
          answer: t('api-available-answer'),
        },
        {
          question: t('Do you offer training for teams?'),
          answer: t(
            'Yes, we provide custom training sessions for teams and enterprises. Contact our support team to schedule a training session.',
          ),
        },
      ],
    },
    {
      key: 'privacy-security',
      title: t('Privacy & Security'),
      items: [
        {
          question: t('How is my data protected?'),
          answer: t('data-protection-answer'),
        },
        {
          question: t('Do you use my content to train AI models?'),
          answer: t(
            'No, we do not use your personal content to train our AI models. Your data remains private and is only used to provide you with our services.',
          ),
        },
        {
          question: t('Can I delete my data?'),
          answer: t(
            'Yes, you can delete the AI outputs on the History page. You can also delete your account by clicking the "Delete Account" button in your account settings.',
          ),
        },
        {
          question: t('Is my payment information secure?'),
          answer: t(
            'Yes, all payment processing is handled securely through Stripe. We do not store your credit card information on our servers.',
          ),
        },
        {
          question: t('Do you comply with GDPR?'),
          answer: t(
            'Yes, we are fully GDPR compliant and respect all data privacy regulations. You can review our privacy policy for detailed information.',
          ),
        },
      ],
    },
  ];

  const faqCategories = hidePurchaseTopics
    ? allCategories.filter(
        (category) => !PURCHASE_CATEGORY_KEYS.includes(category.key),
      )
    : allCategories;


  return (
    <section id='faq'>
      <div className='flex flex-col justify-center'>
        <h2 className='mb-8 text-left text-2xl font-bold leading-8 tracking-tight text-gray-900 dark:text-slate-200'>
          {t('Frequently Asked Questions')}
        </h2>
        <div className='w-full space-y-8'>
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <h3 className='mb-4 text-xl font-bold text-gray-900 dark:text-slate-200'>
                {category.title}
              </h3>
              <div className='space-y-2'>
                {category.items.map((item, itemIndex) => {
                  const questionId = `${categoryIndex}-${itemIndex}`;
                  return (
                    <div
                      key={itemIndex}
                      className='overflow-hidden rounded-lg bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-slate-800'
                    >
                      <button
                        className='flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-slate-700'
                        onClick={() => toggleQuestion(questionId)}
                      >
                        <span className='pr-2 text-base font-medium text-gray-700 dark:text-slate-300'>
                          {item.question}
                        </span>
                        {expandedQuestions.has(questionId) ? (
                          <ChevronUpIcon className='h-5 w-5 flex-shrink-0 text-gray-400 dark:text-slate-500' />
                        ) : (
                          <ChevronDownIcon className='h-5 w-5 flex-shrink-0 text-gray-400 dark:text-slate-500' />
                        )}
                      </button>
                      {expandedQuestions.has(questionId) && (
                        <div className='border-t border-gray-100 px-6 pb-4 pt-2 dark:border-slate-700'>
                          <p
                            className='text-sm text-gray-600 dark:text-slate-400'
                            dangerouslySetInnerHTML={{
                              __html: item.answer as string,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InternalFaq;
